import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Flex } from '@fabric-app/ui/components/Flex';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { styled } from 'styled-components';
import SvgSelfOrganizing from './assets/self-organizing.svg.js';
import SvgElectricity from './assets/electricity.svg.js';
import SvgFabricFaceSkewed from './assets/fabric-face-skewed.svg.js';
import SvgCollaboration from './assets/collaboration.svg.js';
import SvgSearch from './assets/search.svg.js';
import SvgIconFabric from './assets/icon-fabric.svg.js';
import { LimitBadge } from './LimitBadge.js';
import { Button } from '@fabric-app/ui/components/Button';
import { IconSidePanel20Tick } from '@fabric-app/icons/svg';
import { DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { SelectPackage } from './SelectPackage/SelectPackage.js';
import { useState } from 'react';
import { useMutationPurchase, useSubscriptionAccess, useIsRevenueCatAvailable, useMutationRevenueCatRestorePurchase } from '@fabric-app/compat/capabilities/pricing';
import { CanAlterSubscriptionReason } from '../CanAlterSubscriptionReason.js';
import { TypographyContainer, P } from '@fabric-app/ui/components/Typography';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { PlanFeatureList } from '../PlanFeatureList/PlanFeatureList.js';
import { mediaNotMobile, mediaMobile, mediaHover } from '@fabric-app/ui/constants/styled-utils';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { LinkTouch } from '@fabric-app/ui/components/Link/LinkTouch';
import { getPlanExtendedItems } from '../../pricing.config.js';

const upsellItems = [
  {
    Icon: SvgElectricity,
    title: "More thinking credits, more storage, bigger uploads"
  },
  {
    Icon: SvgFabricFaceSkewed,
    title: "More powerful AI assistant to help you write, think, find"
  },
  {
    Icon: SvgSelfOrganizing,
    title: "Fabric organizes itself – your smart workspace"
  },
  {
    Icon: SvgCollaboration,
    title: "Collaborate, review, create together"
  },
  {
    Icon: SvgSearch,
    title: "Find anything instantly with AI search"
  }
];
const NoCommitmentInfo = () => {
  return /* @__PURE__ */ jsxs(ConfirmInfo, {
    children: [
      /* @__PURE__ */ jsx(IconSidePanel20Tick, {}),
      "No commitment, cancel any time."
    ]
  });
};
const UpgradePromptModalContent = ({ title, upsellPlan, limitBadge, currentPlan, onClose }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const { mutate: purchase, isPending: isPurchasePending } = useMutationPurchase();
  const subscriptionAccess = useSubscriptionAccess();
  const isRevenueCatAvailable = useIsRevenueCatAvailable();
  const { isMobileView } = useResponsive();
  const mutationRevenueCatRestorePurchase = useMutationRevenueCatRestorePurchase();
  const isRestorePurchaseAvailable = isRevenueCatAvailable && (subscriptionAccess.canAlterSubscription.allowed || !subscriptionAccess.canAlterSubscription.allowed && subscriptionAccess.canAlterSubscription.reason !== "store_mismatch");
  const onClickRestorePurchase = () => {
    if (!isRestorePurchaseAvailable) return;
    mutationRevenueCatRestorePurchase.mutate(void 0);
  };
  return /* @__PURE__ */ jsxs(MainContainer, {
    width: "lg",
    children: [
      /* @__PURE__ */ jsx(CloseButton, {}),
      /* @__PURE__ */ jsx(ScrollArea.Default, {
        scrollbarVariant: "subtle",
        grow: true,
        expandVertically: true,
        asFlex: true,
        children: /* @__PURE__ */ jsxs(LeftContent, {
          children: [
            /* @__PURE__ */ jsxs(BlurredRingContainer, {
              children: [
                /* @__PURE__ */ jsx(BlurredRing1, {}),
                /* @__PURE__ */ jsx(BlurredRing2, {}),
                /* @__PURE__ */ jsx(BlurredRing3, {}),
                /* @__PURE__ */ jsx(IconFabricStyled, {})
              ]
            }),
            limitBadge && /* @__PURE__ */ jsx(LimitBadge, {
              children: limitBadge
            }),
            /* @__PURE__ */ jsxs(Title, {
              children: [
                title,
                " ",
                upsellPlan ? /* @__PURE__ */ jsxs(Fragment, {
                  children: [
                    /* @__PURE__ */ jsx("br", {}),
                    "with ",
                    /* @__PURE__ */ jsxs(PlanTitleHighlight, {
                      children: [
                        "Fabric ",
                        upsellPlan.title
                      ]
                    })
                  ]
                }) : null
              ]
            }),
            /* @__PURE__ */ jsxs(Desc, {
              children: [
                "Save hours every week",
                /* @__PURE__ */ jsx("br", {}),
                " with your own AI second brain."
              ]
            }),
            /* @__PURE__ */ jsx(InfoBlockContainer, {
              children: upsellItems.map((item) => /* @__PURE__ */ jsxs(InfoBlockItem, {
                children: [
                  /* @__PURE__ */ jsx(InfoBlockItemIconContainer, {
                    children: /* @__PURE__ */ jsx(item.Icon, {})
                  }),
                  /* @__PURE__ */ jsx(InfoBlockItemText, {
                    children: item.title
                  })
                ]
              }, item.title))
            }),
            upsellPlan && /* @__PURE__ */ jsxs(EntitlementsContainer, {
              children: [
                /* @__PURE__ */ jsxs(P, {
                  color: "tertiary",
                  weight: 500,
                  size: "md",
                  children: [
                    "Everything that comes in ",
                    upsellPlan.title,
                    ":"
                  ]
                }),
                /* @__PURE__ */ jsx(PlanFeatureList, {
                  items: getPlanExtendedItems(upsellPlan)
                })
              ]
            })
          ]
        })
      }),
      subscriptionAccess.canAlterSubscription.allowed && upsellPlan ? /* @__PURE__ */ jsxs(RightContent, {
        children: [
          isMobileView && /* @__PURE__ */ jsx(NoCommitmentInfo, {}),
          /* @__PURE__ */ jsx(SelectPlanBlock, {
            children: /* @__PURE__ */ jsx(SelectPackage, {
              currentPlan,
              upsellPlan,
              onSelect: setSelectedPackage,
              selectedPackage
            })
          }),
          /* @__PURE__ */ jsxs(ConfirmBlock, {
            children: [
              !isMobileView && /* @__PURE__ */ jsx(NoCommitmentInfo, {}),
              /* @__PURE__ */ jsx(Button, {
                variant: "bg-primary-reverse",
                shape: "full",
                style: {
                  fontWeight: 600,
                  ...isMobileView && {
                    borderRadius: 13
                  }
                },
                disabled: !selectedPackage,
                isLoading: isPurchasePending,
                size: isMobileView ? "lg" : void 0,
                onClick: () => {
                  if (!selectedPackage) return;
                  purchase({
                    aPackage: selectedPackage
                  });
                },
                children: selectedPackage ? `Continue for ${selectedPackage.packageType === "ANNUAL" ? `${selectedPackage.product.pricePerYearString}/yr` : `${selectedPackage.product.pricePerMonthString}/mo`}` : `Continue`
              }),
              !isMobileView && /* @__PURE__ */ jsx(FooterItem, {
                style: {
                  fontWeight: 500,
                  fontSize: 14
                },
                as: LinkTouch,
                href: "/pricing",
                onClick: onClose,
                children: "See all plans"
              })
            ]
          }),
          isMobileView && /* @__PURE__ */ jsxs(FooterItems, {
            children: [
              isRestorePurchaseAvailable && /* @__PURE__ */ jsx(FooterItem, {
                onClick: onClickRestorePurchase,
                children: "Restore purchase"
              }),
              /* @__PURE__ */ jsx(FooterItem, {
                as: LinkTouch,
                href: "/pricing",
                onClick: onClose,
                children: "See all plans"
              })
            ]
          })
        ]
      }) : /* @__PURE__ */ jsx(RightContent, {
        style: {
          justifyContent: "center",
          textAlign: "center"
        },
        children: /* @__PURE__ */ jsx(CannotAlterSubscriptionReasonContainer, {
          children: /* @__PURE__ */ jsx(CanAlterSubscriptionReason, {})
        })
      })
    ]
  });
};
const MainContainer = styled(Modal.Content).withConfig({
  displayName: "UpgradePromptModalContent__MainContainer",
  componentId: "sc-fe8f80cd-0"
})([
  `overflow:hidden;display:flex;flex-direction:row;border:none;`,
  `{max-height:560px;}`,
  `{flex-direction:column;padding-top:0;}gap:0;`
], mediaNotMobile, mediaMobile);
const LeftContent = styled.div.withConfig({
  displayName: "UpgradePromptModalContent__LeftContent",
  componentId: "sc-fe8f80cd-1"
})([
  `flex-grow:1;background:linear-gradient( 180deg,rgba(`,
  `,0) 0%,rgba(`,
  `,0.03) 100% );border-right:1px solid rgba(`,
  `,0.05);overflow:hidden;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:30px;padding:2rem;padding-top:calc(2rem + env(safe-area-inset-top));`,
  `{padding:1.5rem;padding-bottom:2rem;padding-top:max(2rem,env(safe-area-inset-top));border-right:none;}`
], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-primary-reverse-rgb"], mediaMobile);
const Title = styled(DialogTitle).withConfig({
  displayName: "UpgradePromptModalContent__Title",
  componentId: "sc-fe8f80cd-2"
})([
  `font-family:'Manrope',sans-serif;font-size:26px;font-weight:800;line-height:36px;text-align:center;color:`,
  `;`
], cssVar["color-text-primary"]);
const PlanTitleHighlight = styled.span.withConfig({
  displayName: "UpgradePromptModalContent__PlanTitleHighlight",
  componentId: "sc-fe8f80cd-3"
})([
  `font-family:'Manrope',sans-serif;color:`,
  `;`
], cssVar["color-app-primary-text"]);
const Desc = styled(DialogDescription).withConfig({
  displayName: "UpgradePromptModalContent__Desc",
  componentId: "sc-fe8f80cd-4"
})([
  `font-size:14px;font-weight:500;line-height:20px;text-align:center;color:rgba(`,
  `,0.6);`
], cssVar["color-text-tertiary-rgb"]);
const InfoBlockContainer = styled(Flex).attrs({
  direction: "column"
}).withConfig({
  displayName: "UpgradePromptModalContent__InfoBlockContainer",
  componentId: "sc-fe8f80cd-5"
})([
  `border-radius:24px 24px;position:relative;background:`,
  `;box-shadow:inset 0 0 0 2px rgba(`,
  `,0.05);`,
  `{box-shadow:none;}`
], cssVar["color-bg-primary"], cssVar["color-bg-primary-reverse-rgb"], mediaMobile);
const InfoBlockItem = styled(Flex).attrs({
  alignItems: "center"
}).withConfig({
  displayName: "UpgradePromptModalContent__InfoBlockItem",
  componentId: "sc-fe8f80cd-6"
})([
  `width:100%;gap:1.5rem;padding:0 0.75rem;height:72px;`
]);
const InfoBlockItemIconContainer = styled(Flex).attrs({
  alignItems: "center",
  justifyContent: "center"
}).withConfig({
  displayName: "UpgradePromptModalContent__InfoBlockItemIconContainer",
  componentId: "sc-fe8f80cd-7"
})([
  `flex-shrink:0;width:48px;height:48px;color:`,
  `;`
], cssVar["color-text-primary"]);
const InfoBlockItemText = styled.div.withConfig({
  displayName: "UpgradePromptModalContent__InfoBlockItemText",
  componentId: "sc-fe8f80cd-8"
})([
  `font-size:14px;font-weight:500;line-height:20px;color:`,
  `;`
], cssVar["color-text-primary"]);
const RightContent = styled(Flex).withConfig({
  displayName: "UpgradePromptModalContent__RightContent",
  componentId: "sc-fe8f80cd-9"
})([
  `background:`,
  `;width:300px;padding:1rem 1.5rem;flex-direction:column;gap:1rem;position:relative;`,
  `{width:100%;padding-top:0.5rem;align-items:center;&:before{content:'';position:absolute;bottom:calc(100% - 1px);left:0;width:100%;height:70px;background:linear-gradient( to bottom,rgba(`,
  `,0),rgba(`,
  `,1) 70% );}}`
], cssVar["color-bg-primary"], mediaMobile, cssVar["color-bg-primary-rgb"], cssVar["color-bg-primary-rgb"]);
const SelectPlanBlock = styled(Flex).attrs({
  gap: 12,
  alignItems: "center",
  justifyContent: "center",
  direction: "column"
}).withConfig({
  displayName: "UpgradePromptModalContent__SelectPlanBlock",
  componentId: "sc-fe8f80cd-10"
})([
  `width:100%;flex-grow:1;`,
  `{padding-top:1.5rem;}`
], mediaNotMobile);
const ConfirmBlock = styled(Flex).attrs({
  gap: 12,
  alignItems: "center",
  direction: "column"
}).withConfig({
  displayName: "UpgradePromptModalContent__ConfirmBlock",
  componentId: "sc-fe8f80cd-11"
})([
  `width:100%;`
]);
const ConfirmInfo = styled(Flex).attrs({
  alignItems: "center",
  gap: "element"
}).withConfig({
  displayName: "UpgradePromptModalContent__ConfirmInfo",
  componentId: "sc-fe8f80cd-12"
})([
  `font-size:14px;font-weight:500;line-height:20px;color:`,
  `;`
], cssVar["color-text-tertiary"]);
const CannotAlterSubscriptionReasonContainer = styled(Flex).attrs({
  direction: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12
}).withConfig({
  displayName: "UpgradePromptModalContent__CannotAlterSubscriptionReasonContainer",
  componentId: "sc-fe8f80cd-13"
})([
  `width:100%;font-weight:500;padding:2rem 0;border-top:2px solid `,
  `;border-bottom:2px solid `,
  `;`,
  `{gap:12px;}`,
  `{font-size:15px;font-weight:500;}`
], cssVar["color-border-primary"], cssVar["color-border-primary"], TypographyContainer, P);
const EntitlementsContainer = styled(Flex).attrs({
  gap: "elementsContainer",
  direction: "column"
}).withConfig({
  displayName: "UpgradePromptModalContent__EntitlementsContainer",
  componentId: "sc-fe8f80cd-14"
})([
  `width:100%;background:`,
  `;border-radius:24px;padding:2rem;`
], cssVar["color-bg-primary"]);
const FooterItems = styled(Flex).attrs({
  gap: "elementsContainer",
  justifyContent: "center"
}).withConfig({
  displayName: "UpgradePromptModalContent__FooterItems",
  componentId: "sc-fe8f80cd-15"
})([
  ``
]);
const FooterItem = styled.span.withConfig({
  displayName: "UpgradePromptModalContent__FooterItem",
  componentId: "sc-fe8f80cd-16"
})([
  `font-size:15px;font-weight:600;line-height:20px;color:rgba(`,
  `,0.6);cursor:pointer;`,
  `{&:hover{color:rgba(`,
  `,0.4);}}`
], cssVar["color-text-primary-rgb"], mediaHover, cssVar["color-text-primary-rgb"]);
const BlurredRingContainer = styled(Flex).attrs({
  alignItems: "center",
  justifyContent: "center"
}).withConfig({
  displayName: "UpgradePromptModalContent__BlurredRingContainer",
  componentId: "sc-fe8f80cd-17"
})([
  `position:relative;height:156px;width:100%;display:flex;align-items:center;justify-content:center;`,
  `{display:none;}`
], mediaNotMobile);
const BlurredRing3 = styled.div.withConfig({
  displayName: "UpgradePromptModalContent__BlurredRing3",
  componentId: "sc-fe8f80cd-18"
})([
  `position:absolute;width:706.25px;height:706.25px;left:50%;top:-610px;border-radius:50%;transform:translateX(-50%);border:57.0179px solid #1500ff;filter:blur(41.7596px);opacity:0.77;`
]);
const BlurredRing2 = styled.div.withConfig({
  displayName: "UpgradePromptModalContent__BlurredRing2",
  componentId: "sc-fe8f80cd-19"
})([
  `position:absolute;width:706.25px;height:706.25px;left:50%;top:-580px;border-radius:50%;transform:translateX(-50%);border:8.03069px solid #1500ff;filter:blur(41.7596px);`
]);
const BlurredRing1 = styled.div.withConfig({
  displayName: "UpgradePromptModalContent__BlurredRing1",
  componentId: "sc-fe8f80cd-20"
})([
  `position:absolute;width:764.43px;height:764.43px;left:50%;top:-730px;border-radius:50%;transform:translateX(-50%);border:32.9258px solid rgba(255,0,208,0.7);opacity:0.7;filter:blur(41.7596px);z-index:20;`
]);
const IconFabricStyled = styled(SvgIconFabric).withConfig({
  displayName: "UpgradePromptModalContent__IconFabricStyled",
  componentId: "sc-fe8f80cd-21"
})([
  `position:relative;z-index:30;`
]);
const CloseButton = styled(Modal.Close).withConfig({
  displayName: "UpgradePromptModalContent__CloseButton",
  componentId: "sc-fe8f80cd-22"
})([
  ``,
  `{display:none;}position:absolute;top:max(env(safe-area-inset-top),12px);right:12px;z-index:30;background:rgba(`,
  `,0.1);color:rgba(`,
  `,1);`
], mediaNotMobile, cssVar["color-bg-primary-reverse-rgb"], cssVar["color-text-primary-reverse-rgb"]);

export { UpgradePromptModalContent };
//# sourceMappingURL=UpgradePromptModalContent.js.map
